"use client";

import Link from "next/link";

const LAST_UPDATED = "17 February 2026";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#0b1021] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0b1021]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 text-lg font-bold">
            <span className="text-emerald-400">Amp</span>Verve
          </Link>
          <Link
            href="/login"
            className="text-sm text-slate-400 hover:text-white transition"
          >
            Back to App
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-16">
        {/* Privacy Policy */}
        <section>
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
            <p className="text-slate-400">Last updated: {LAST_UPDATED}</p>
          </div>

          <div className="space-y-8 text-slate-300 leading-relaxed">
            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                1. Data We Collect
              </h2>
              <p>
                We collect the following categories of data to provide our smart
                EV charging and V2G services:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-slate-400">
                <li>
                  Account information: name, email address, and organisation
                  details
                </li>
                <li>
                  Vehicle data: battery level, charge state, and location when
                  connected to a charger
                </li>
                <li>
                  Energy data: charging history, tariff rates, carbon intensity
                  of consumed energy
                </li>
                <li>
                  Payment information: processed securely via Stripe — we do not
                  store card numbers
                </li>
              </ul>
              <p className="mt-2 text-sm text-slate-500">
                We do not track your vehicle location when it is not connected to
                a charging session.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                2. How We Use Your Data
              </h2>
              <ul className="list-disc list-inside space-y-1 text-slate-400">
                <li>
                  Optimise charging schedules to minimise your costs and carbon
                  footprint
                </li>
                <li>
                  Calculate V2G earnings and provide settlement reports
                </li>
                <li>
                  Generate carbon impact reports and sustainability certificates
                </li>
                <li>Improve our algorithms and service reliability</li>
                <li>Send relevant notifications about charging events</li>
              </ul>
              <p className="mt-2 font-medium text-white">
                We never sell your personal data to third parties.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                3. Data Storage &amp; Security
              </h2>
              <p>
                All data is encrypted in transit using TLS 1.3 and at rest using
                AES-256 encryption. Data is stored on Microsoft Azure servers in
                the EU (West Europe region). We conduct regular security audits
                and penetration testing.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                4. Third-Party Services
              </h2>
              <p>We share limited data with the following services:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-slate-400">
                <li>
                  <strong>Smartcar</strong> — vehicle connectivity and telemetry
                </li>
                <li>
                  <strong>Stripe</strong> — payment processing
                </li>
                <li>
                  <strong>Your energy supplier</strong> — tariff optimisation
                  (with your consent)
                </li>
              </ul>
              <p className="mt-2 text-sm text-slate-500">
                Each third party operates under their own privacy policy.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                5. Your Rights (GDPR)
              </h2>
              <p>Under the UK and EU GDPR, you have the right to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-slate-400">
                <li>Access all data we hold about you</li>
                <li>Correct inaccurate personal data</li>
                <li>Delete your account and all associated data</li>
                <li>Export your data in a portable format (JSON/CSV)</li>
                <li>Withdraw consent at any time</li>
                <li>Object to automated decision-making</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                6. Data Retention
              </h2>
              <p>
                We retain your data for as long as your account is active. After
                account deletion, all personal data is permanently removed within
                30 days. Anonymised usage statistics may be retained for service
                improvement.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                7. Cookies
              </h2>
              <p>
                We use essential cookies for authentication and session
                management. We do not use third-party tracking cookies or
                advertising trackers.
              </p>
            </div>
          </div>
        </section>

        {/* Separator */}
        <hr className="border-white/10" />

        {/* Terms of Service */}
        <section>
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
            <p className="text-slate-400">Last updated: {LAST_UPDATED}</p>
          </div>

          <div className="space-y-8 text-slate-300 leading-relaxed">
            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                1. Acceptance of Terms
              </h2>
              <p>
                By using AmpVerve, you agree to these Terms of Service. The
                service is provided by AmpVerve Ltd, a company registered in
                England and Wales.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                2. Service Description
              </h2>
              <p>
                AmpVerve provides smart EV charging optimisation,
                vehicle-to-grid (V2G) energy trading, fleet energy management,
                and demand-side response services. We connect to your vehicle via
                authorised manufacturer APIs to manage charging schedules.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                3. User Accounts
              </h2>
              <p>
                You are responsible for maintaining the security of your account
                credentials. You must provide accurate information during
                registration. Account sharing is not permitted.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                4. Charging &amp; V2G
              </h2>
              <p>
                AmpVerve optimises charging based on your preferences, real-time
                energy prices, and grid conditions. V2G export is optional and
                will never discharge your vehicle below your configured minimum
                level. V2G earnings are credited to your AmpVerve wallet and can
                be withdrawn at any time.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                5. Battery Health
              </h2>
              <p>
                Our optimisation algorithms are designed to protect battery
                longevity. We follow manufacturer-recommended charging limits and
                provide battery health monitoring. However, AmpVerve is not
                responsible for battery degradation beyond normal wear and tear.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                6. Payments &amp; Billing
              </h2>
              <p>
                Payments are processed securely via Stripe. Subscription fees are
                billed monthly. Free tier includes basic smart charging. Premium
                features (V2G, fleet management, advanced analytics) require a
                paid subscription. Refunds are available within 14 days of
                purchase if the service has not been materially used.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                7. Limitation of Liability
              </h2>
              <p>
                Our liability is limited to the fees you have paid in the
                previous 12 months. AmpVerve is not liable for losses caused by
                third-party services, grid outages, vehicle manufacturer API
                changes, or force majeure events.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">
                8. Termination
              </h2>
              <p>
                You may cancel your account at any time. Upon cancellation, any
                remaining V2G earnings will be settled within 30 days. We may
                suspend accounts that violate these terms.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-slate-800/30 border border-white/10 rounded-2xl p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">Questions?</h2>
          <p className="text-slate-400 mb-4">
            Contact our Data Protection Officer for privacy enquiries
          </p>
          <a
            href="mailto:privacy@ampverve.com"
            className="inline-block bg-emerald-500/20 text-emerald-400 px-6 py-3 rounded-xl font-medium hover:bg-emerald-500/30 transition"
          >
            privacy@ampverve.com
          </a>
        </section>

        <div className="h-8" />
      </main>
    </div>
  );
}
